import { useRef, useCallback, useEffect } from "react";

// ── FRASES DE CIERRE ──────────────────────────────────────────────────────
// Reglas:
// 1. La frase debe aparecer al FINAL de la transcripción (endsWith), no
//    en cualquier parte. Esto evita falsos positivos como "responda
//    nada más con sí o no" → match con "nada más" en medio de la oración.
// 2. Las frases están en forma NORMALIZADA: minúsculas, sin tildes, sin
//    puntuación. La función normalize() abajo aplica la misma transformación
//    al input antes de comparar — así "señoría" y "senoria" matchean igual.
// 3. Solo frases inequívocas que en habla natural NO aparecen a media
//    oración. Removidas: "nada más", "sin más" sueltas, "hasta ahí" sola,
//    "termino aquí", "finalizo" — todas pueden aparecer en instrucciones
//    al testigo y disparaban cierres falsos.
const CLOSING_PHRASES = [
  // ── No tengo más preguntas (variantes) ───────────────────────────────
  "no tengo mas preguntas",
  "no tengo mas preguntas senoria",
  "no tengo mas preguntas su senoria",
  "no tengo mas preguntas senor juez",
  "no tengo mas preguntas senora juez",
  "no tengo preguntas",
  "no tengo preguntas senoria",
  "no tengo preguntas su senoria",
  "no mas preguntas",
  "no mas preguntas senoria",
  "no mas preguntas su senoria",
  "sin mas preguntas",
  "sin mas preguntas senoria",
  "sin mas preguntas su senoria",
  "no hay mas preguntas",
  "no hay mas preguntas senoria",
  "no hay mas preguntas su senoria",
  "no tengo nada mas que preguntar",
  "no tengo nada mas que preguntarle",
  "no tengo nada mas que preguntar al testigo",
  "no tengo nada mas que preguntar a la testigo",

  // ── Eso es todo / es todo ────────────────────────────────────────────
  "es todo senoria",
  "es todo su senoria",
  "es todo de mi parte",
  "es todo de mi parte senoria",
  "es todo de mi parte su senoria",
  "es todo por mi parte",
  "eso es todo",
  "eso es todo senoria",
  "eso es todo su senoria",
  "eso es todo de mi parte",
  "eso es todo de mi parte senoria",

  // ── Es cuanto (formal forense ecuatoriano) ───────────────────────────
  "es cuanto",
  "es cuanto senoria",
  "es cuanto su senoria",
  "es cuanto puedo manifestar",
  "es cuanto puedo manifestar senoria",
  "es cuanto puedo expresar",

  // ── He terminado / hemos terminado / he concluido ────────────────────
  "he terminado",
  "he terminado senoria",
  "he terminado su senoria",
  "he terminado mi interrogatorio",
  "he terminado mi contrainterrogatorio",
  "he terminado el interrogatorio",
  "he terminado el contrainterrogatorio",
  "hemos terminado",
  "hemos terminado senoria",
  "hemos terminado su senoria",
  "he concluido",
  "he concluido senoria",
  "he concluido su senoria",
  "he concluido mi interrogatorio",
  "he concluido mi contrainterrogatorio",
  "he concluido el interrogatorio",
  "he concluido el contrainterrogatorio",

  // ── Concluyo / termino el interrogatorio ─────────────────────────────
  "concluyo senoria",
  "concluyo mi interrogatorio",
  "concluyo el interrogatorio",
  "concluyo mi contrainterrogatorio",
  "concluyo el contrainterrogatorio",
  "termino mi interrogatorio",
  "termino el interrogatorio",
  "termino mi contrainterrogatorio",
  "termino el contrainterrogatorio",
  "doy por terminado mi interrogatorio",
  "doy por terminado el interrogatorio",
  "doy por terminado mi contrainterrogatorio",
  "doy por terminado el contrainterrogatorio",
  "doy por concluido mi interrogatorio",
  "doy por concluido el interrogatorio",
  "doy por concluido mi contrainterrogatorio",
  "doy por concluido el contrainterrogatorio",

  // ── Hasta aquí mis preguntas (con "preguntas" obligatorio) ───────────
  "hasta aqui mis preguntas",
  "hasta ahi mis preguntas",
  "hasta aqui llegan mis preguntas",
  "hasta ahi llegan mis preguntas",
  "hasta aqui mis preguntas senoria",
  "hasta ahi mis preguntas senoria",

  // ── Puede retirarse el testigo (orden directa al juez) ───────────────
  "puede retirarse",
  "puede retirarse senoria",
  "puede retirarse el testigo",
  "puede retirarse la testigo",
  "el testigo puede retirarse",
  "la testigo puede retirarse",

  // ── Renuncio a continuar (cortés) ────────────────────────────────────
  "renuncio a continuar",
  "renuncio al interrogatorio",
  "renuncio al contrainterrogatorio",
];

// Normaliza para comparación: minúsculas, sin acentos, sin puntuación,
// espacios colapsados. Aplica la misma transformación a la lista y al input.
function normalize(text) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")        // quita tildes y diacríticos
    .replace(/[.,;:!?¡¿"'()-]/g, " ")      // puntuación → espacio
    .replace(/\s+/g, " ")                   // espacios múltiples → uno
    .trim();
}

export function isTurnEnd(text) {
  if (!text) return false;
  const cleaned = normalize(text);
  if (!cleaned) return false;
  // Match si la frase ES toda la transcripción O termina con la frase
  // (precedida de espacio, así "no preguntas" no matchea "preguntas")
  return CLOSING_PHRASES.some(s => cleaned === s || cleaned.endsWith(" " + s));
}

export function useMicrophone({ onTranscript }) {
  const streamRef       = useRef(null);
  const wsRef           = useRef(null);
  const recorderRef     = useRef(null);
  const apiKeyRef       = useRef(null);
  const listeningRef    = useRef(false);
  const onTranscriptRef = useRef(onTranscript);

  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);

  const getKey = useCallback(async () => {
    if (apiKeyRef.current) return apiKeyRef.current;
    const res  = await fetch("/api/deepgram-token");
    const data = await res.json();
    if (!data.key) throw new Error("DEEPGRAM_API_KEY no configurada en Vercel");
    apiKeyRef.current = data.key;
    return data.key;
  }, []);

  const initStream = useCallback(async () => {
    if (streamRef.current) return;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 },
    });
    streamRef.current = stream;
  }, []);

  const startListening = useCallback(async () => {
    if (listeningRef.current) return;
    listeningRef.current = true;

    try {
      const [key] = await Promise.all([getKey(), initStream()]);
      if (!listeningRef.current) return;

      // URL mínima — solo parámetros básicos garantizados en todos los planes
      const url = `wss://api.deepgram.com/v1/listen?model=nova-2&language=es&punctuate=true&smart_format=true&endpointing=500&interim_results=false`;

      // Autenticación via subprotocolo — método oficial documentado por Deepgram
      const ws = new WebSocket(url, ["token", key]);
      wsRef.current = ws;

      await new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          reject(new Error("Timeout — Deepgram no respondió en 8s. Verifica la API key en Vercel."));
        }, 8000);

        ws.onopen = () => {
          clearTimeout(timer);
          console.log("✅ Deepgram WebSocket conectado");
          resolve();
        };

        ws.onerror = () => {
          clearTimeout(timer);
          reject(new Error("Deepgram rechazó la conexión — verifica que la API key esté actualizada en Vercel"));
        };

        ws.onclose = (e) => {
          clearTimeout(timer);
          if (!ws._opened) {
            reject(new Error(`Deepgram cerró sin abrir (código ${e?.code}) — API key inválida o plan sin streaming`));
          }
        };
      });
      ws._opened = true;

      if (!listeningRef.current) { ws.close(); return; }

      ws.onmessage = e => {
        if (!listeningRef.current) return;
        try {
          const data = JSON.parse(e.data);
          if (data.type === "Results" && data.is_final) {
            const t = data.channel?.alternatives?.[0]?.transcript?.trim();
            if (t) onTranscriptRef.current(t);
          }
        } catch { /* ignore */ }
      };
      ws.onerror = e => console.warn("Deepgram error en sesión:", e);
      ws.onclose = () => { console.log("Deepgram WS cerrado"); };

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus" : "audio/webm";
      const recorder = new MediaRecorder(streamRef.current, { mimeType });
      recorderRef.current = recorder;

      recorder.ondataavailable = e => {
        if (e.data?.size > 0 && ws.readyState === WebSocket.OPEN) {
          ws.send(e.data);
        }
      };
      recorder.start(100);

    } catch (e) {
      console.error("startListening falló:", e.message);
      listeningRef.current = false;
      throw e;
    }
  }, [getKey, initStream]);

  const stopListening = useCallback(() => {
    listeningRef.current = false;
    if (recorderRef.current?.state !== "inactive") {
      try { recorderRef.current.stop(); } catch {}
    }
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try { wsRef.current.send(JSON.stringify({ type: "CloseStream" })); } catch {}
      wsRef.current.close();
    }
    recorderRef.current = null;
    wsRef.current = null;
  }, []);

  const destroyMic = useCallback(() => {
    stopListening();
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    apiKeyRef.current = null;
  }, [stopListening]);

  useEffect(() => () => destroyMic(), [destroyMic]);
  return { startListening, stopListening, destroyMic };
}
